import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Eye, Loader2 } from 'lucide-react';
import api from '../../../utils/api';
import {
  assertBlobKind,
  filenameFromDisposition,
  parseBlobError,
} from '../../../utils/blobDownload';
import { useToast } from '../../../hooks/use-toast';
import { LEAVE_STATUS_LABELS } from './attendanceStatus';

const STATUS_VARIANT = {
  pending: 'secondary',
  pending_class_teacher: 'secondary',
  pending_admin: 'outline',
  approved: 'default',
  rejected: 'destructive',
  more_info: 'outline',
};

function LeaveRow({ item, onPreview, loadingId }) {
  return (
    <li className="rounded-lg border border-border p-3 text-sm space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">
          {item.leave_from}
          {' '}
          →
          {' '}
          {item.leave_to}
        </span>
        <Badge variant={STATUS_VARIANT[item.status] || 'secondary'}>
          {item.status_label || LEAVE_STATUS_LABELS[item.status] || item.status}
        </Badge>
      </div>
      <p className="text-muted-foreground capitalize">
        {item.leave_type}
        {' '}
        ·
        {' '}
        {item.days_count}
        {' '}
        day(s)
      </p>
      {item.reason ? (
        <p className="text-muted-foreground line-clamp-2">{item.reason}</p>
      ) : null}
      {item.reviewer_remarks ? (
        <p className="text-xs text-destructive">Remarks: {item.reviewer_remarks}</p>
      ) : null}
      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={loadingId === item.id}
          onClick={() => onPreview(item)}
        >
          {loadingId === item.id ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Eye className="h-3 w-3 mr-1" />
          )}
          Preview
        </Button>
      </div>
    </li>
  );
}

export default function LeaveHistoryPanel({
  parentMode,
  studentId,
  refreshKey = 0,
  onPdfReady,
}) {
  const { toast } = useToast();
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    if (parentMode && !studentId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const base = parentMode
        ? '/portal/parent/leave-applications'
        : '/portal/student/leave-applications';
      const params = {
        ...(parentMode ? { student_id: studentId } : {}),
        ...(tab !== 'all' ? { status: tab } : {}),
      };
      const res = await api.get(base, { params });
      setItems(res.data?.data ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [parentMode, studentId, tab]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const previewPdf = async (item) => {
    if (!item?.id) return;
    const base = parentMode
      ? `/portal/parent/leave-applications/${item.id}/pdf`
      : `/portal/student/leave-applications/${item.id}/pdf`;
    const params = parentMode ? { student_id: studentId } : undefined;
    setDownloadingId(item.id);
    try {
      const res = await api.get(base, { params, responseType: 'blob' });
      const blob = res.data;
      const msg = await parseBlobError(blob);
      if (msg) {
        throw new Error(msg);
      }
      await assertBlobKind(blob, 'pdf');
      const filename = filenameFromDisposition(
        res.headers['content-disposition'],
        `leave-${item.id}.pdf`,
      );
      const title = `Leave application — ${item.leave_from} → ${item.leave_to}`;
      onPdfReady?.(blob, filename, title);
    } catch (e) {
      toast({
        title: 'Preview failed',
        description: e?.message || 'Could not load PDF',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = tab === 'all'
    ? items
    : items.filter((i) => i.status === tab);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Leave history</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-3 flex flex-wrap h-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending_class_teacher">Class teacher</TabsTrigger>
            <TabsTrigger value="pending_admin">Admin</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="more_info">More info</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-0">
            {loading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">No leave applications.</p>
            ) : (
              <ul className="space-y-2">
                {filtered.map((item) => (
                  <LeaveRow
                    key={item.id}
                    item={item}
                    loadingId={downloadingId}
                    onPreview={(row) => {
                      if (downloadingId === row.id) return;
                      previewPdf(row);
                    }}
                  />
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
